/**
 * Elio Vieri — percentage-based Shipping Protection.
 *
 * The hidden product "elio-vieri-shipping-protection" has one variant per price tier. When the customer turns
 * protection on, this adds the first tier priced at or above 3% of the cart subtotal (protection excluded).
 * Whenever the cart changes (quantity updates, adds, removes) the tier is recomputed and swapped if needed;
 * if protection is the only thing left in the cart it is removed. Default state is OFF.
 *
 * Tiers come from <script type="application/json" data-ev-protection-tiers> (snippets/ev-protection.liquid).
 * All requests use the Ajax Cart API under window.Shopify.routes.root so they work on localized URLs.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const HANDLE = 'elio-vieri-shipping-protection';
const RATE_BASIS_POINTS = 500; // 5.00%
const LEGACY_CART_EVENTS = ['cart:update', 'cart:updated'];
const DEBOUNCE_MS = 250;

/** Events this module dispatched itself; ignored by our own listener so we never loop. */
const ownEvents = new WeakSet();

/** @type {{id: number, price: number}[] | null} */
let tierCache = null;
let debounceTimer = 0;
/** Serialises every cart mutation this module makes. */
let queue = Promise.resolve();

function route(path) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/?$/, '/') + path;
}

function readTiers() {
  const node = document.querySelector('script[data-ev-protection-tiers]');
  if (node) {
    try {
      const parsed = JSON.parse(node.textContent || '[]')
        .filter((t) => t && t.id && Number.isFinite(t.price))
        .sort((a, b) => a.price - b.price);
      if (parsed.length) tierCache = parsed;
    } catch (error) {
      console.warn('[ev-protection] Could not read tiers', error);
    }
  }
  return tierCache;
}

/** First tier priced at or above 3% of the subtotal (cents); the top tier caps very large carts. */
function pickTier(tiers, subtotalCents) {
  for (const tier of tiers) {
    if (tier.price * 10000 >= subtotalCents * RATE_BASIS_POINTS) return tier;
  }
  return tiers[tiers.length - 1];
}

function splitCart(cart, tiers) {
  const tierIds = new Set(tiers.map((t) => String(t.id)));
  const protection = [];
  const others = [];
  for (const item of cart.items || []) {
    if (item.handle === HANDLE || tierIds.has(String(item.variant_id))) protection.push(item);
    else others.push(item);
  }
  return { protection, others };
}

/**
 * Works out the /cart/update.js "updates" map (variant id -> quantity) that brings the cart to the right state.
 * @param {boolean} [wantOn] - Explicit toggle state; when omitted, keep protection only if it is already in the cart.
 */
function computeUpdates(cart, tiers, wantOn) {
  const { protection, others } = splitCart(cart, tiers);
  const on = typeof wantOn === 'boolean' ? wantOn : protection.length > 0;
  /** @type {Record<string, number>} */
  const updates = {};

  if (!on || others.length === 0) {
    for (const item of protection) updates[item.variant_id] = 0;
    return updates;
  }

  const subtotal = others.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);
  const target = pickTier(tiers, subtotal);
  let kept = false;

  for (const item of protection) {
    if (String(item.variant_id) === String(target.id) && !kept) {
      kept = true;
      if (item.quantity !== 1) updates[item.variant_id] = 1;
    } else if (String(item.variant_id) !== String(target.id)) {
      updates[item.variant_id] = 0;
    }
  }
  if (!kept) updates[target.id] = 1;
  return updates;
}

async function fetchCart() {
  const response = await fetch(route('cart.js'), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function setBusy(busy) {
  document.querySelectorAll('[data-ev-protection]').forEach((el) => el.setAttribute('aria-busy', String(busy)));
  document.querySelectorAll('input[data-ev-protection-toggle]').forEach((input) => {
    if (input instanceof HTMLInputElement) input.disabled = busy;
  });
}

function syncToggles(cart, tiers) {
  const { protection } = splitCart(cart, tiers);
  document.querySelectorAll('input[data-ev-protection-toggle]').forEach((input) => {
    if (input instanceof HTMLInputElement) input.checked = protection.length > 0;
  });
}

/** Sends the updates, then lets the theme re-render the cart (drawer + cart page) through its own event pipeline. */
async function applyUpdates(updates) {
  const sections = cartSectionIds();
  const body = { updates };
  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  const lines = Object.entries(updates).map(([id, quantity]) => ({ id: String(id), quantity }));
  const allRemoved = lines.every((line) => line.quantity === 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  let dispatched = false;

  try {
    // 'update'/'remove' (never 'add') so the drawer does not pop open on background tier swaps.
    const event = new CartLinesUpdateEvent({
      action: allRemoved ? 'remove' : 'update',
      context: 'cart',
      lines,
      promise: deferred.promise,
    });
    ownEvents.add(event);
    document.dispatchEvent(event);
    dispatched = true;
  } catch (error) {
    console.warn('[ev-protection] Could not dispatch cart event, falling back to direct re-render', error);
  }

  try {
    const response = await fetch(route('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (!response.ok || json.status || json.errors) {
      throw new Error(json.description || json.message || json.errors || `cart/update.js ${response.status}`);
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
      detail: {
        sections: json.sections,
        items: json.items,
        itemCount: json.item_count,
        source: 'ev-protection',
        didError: false,
      },
    });

    if (!dispatched && json.sections) {
      for (const id of sections) {
        if (json.sections[id]) {
          morphSection(id, json.sections[id], { mode: id === 'cart-drawer-section' ? 'hydration' : 'full' });
        }
      }
    }
    return json;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/** @param {boolean} [wantOn] */
function enqueue(wantOn) {
  const task = async () => {
    const tiers = readTiers();
    if (!tiers || !tiers.length) return;
    setBusy(true);
    try {
      const cart = await fetchCart();
      const updates = computeUpdates(cart, tiers, wantOn);
      const finalCart = Object.keys(updates).length ? await applyUpdates(updates) : cart;
      // Sections are morphed after the promise resolves; sync once the DOM has settled.
      requestAnimationFrame(() => syncToggles(finalCart, tiers));
    } catch (error) {
      console.warn('[ev-protection]', error);
      try {
        syncToggles(await fetchCart(), tiers);
      } catch (_) {
        /* ignore */
      }
    } finally {
      setBusy(false);
    }
  };
  queue = queue.then(task, task);
  return queue;
}

function scheduleReconcile() {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => enqueue(), DEBOUNCE_MS);
}

/** Any cart change made elsewhere in the theme: wait for it to land, then re-check the tier. */
function onCartChanged(event) {
  if (ownEvents.has(event)) return;
  const pending = event && event.promise;
  if (pending && typeof pending.then === 'function') {
    pending.then(scheduleReconcile, scheduleReconcile);
  } else {
    window.setTimeout(scheduleReconcile, 400);
  }
}

function init() {
  if (window.__evProtectionReady) return;
  window.__evProtectionReady = true;

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[data-ev-protection-toggle]')) return;
    window.clearTimeout(debounceTimer);
    enqueue(input.checked);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartChanged);
  for (const name of LEGACY_CART_EVENTS) document.addEventListener(name, onCartChanged);

  // Correct any stale tier left from an earlier session (e.g. the cart changed on another page).
  if (document.querySelector('[data-ev-protection]')) scheduleReconcile();
}

init();
